import { SeoSpecialty, SeoExam } from '@medprev-web-app/api-graphql-client';
import { faCalendarCheck } from '@fortawesome/pro-light-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';
import Link from 'next/link';
import { useMemo } from 'react';
import { useStore } from 'zustand';

import { toTelehealthSearchPath } from '@/app/(main)/(pages)/(institucional)/(index)/_components/cta/_utils/telehealth-search-path.util';
import { Button } from '@/components/ui/button/button.component';
import { useNavigation } from '@/hooks/navigation/use-navigation.hook';
import { Place } from '@/types/google-maps.types';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { userLocationStore } from '@/store/user-location.store';
import { PersistedUserNavigationSearchParams } from '@/types/enums/persisted-user-navigation-search-params.enum';

interface CtaProcedureTelemedicineButtonProps extends React.HTMLAttributes<HTMLAnchorElement> {
  title?: string;
  telehealthUrl?: string | null;
  citySlug?: string;
  onlyTelemedicine?: boolean;
  combinedWithOthers?: boolean;
  withStartIcon?: boolean;
  procedure?: SeoSpecialty | SeoExam | null;
  place: Pick<Place, 'state' | 'city'>;
  originCta?: string;
}

export function CtaProcedureTelemedicineButton({
  title = 'Agendar teleconsulta',
  onlyTelemedicine,
  telehealthUrl,
  combinedWithOthers,
  citySlug,
  withStartIcon = true,
  procedure,
  place,
  originCta,
  ...rest
}: CtaProcedureTelemedicineButtonProps) {
  const userLocation = useStore(userLocationStore, (state) => state.userLocation);
  const { buildLink } = useNavigation();
  const styleProps = onlyTelemedicine
    ? { variant: 'default' as const }
    : combinedWithOthers
      ? { variant: 'secondary' as const, color: 'primary' as const }
      : undefined;
  const buttonIcon = withStartIcon ? faCalendarCheck : undefined;

  const buildTelehealthUrl = useMemo((): string => {
    if (!telehealthUrl) {
      return '';
    }

    const searchPath = toTelehealthSearchPath(telehealthUrl);

    if (searchPath) {
      const cityOverride = citySlug ? { [PersistedUserNavigationSearchParams.CIDADE]: citySlug } : undefined;
      return buildLink({ path: searchPath, overrides: cityOverride });
    }

    const url = new URL(telehealthUrl);

    if (citySlug) {
      url.searchParams.set('cidade', citySlug);
    }

    if (userLocation?.formattedAddress) {
      url.searchParams.set(PersistedUserNavigationSearchParams.LOCATION, JSON.stringify(userLocation));
    }

    return url.toString();
  }, [buildLink, citySlug, telehealthUrl, userLocation]);

  return (
    <>
      {telehealthUrl && (
        <Link
          prefetch={false}
          data-testid={DataTestIdEnum.SEO_CTA_TELEMEDICINE_BUTTON}
          href={buildTelehealthUrl}
          data-analytics-event={
            AnalyticsEnum.CLICK_CTA_SCHEDULE_SPECIALTY_BY_TELEHEALTH
          }
          data-analytics-properties={JSON.stringify({
            secao_origem: originCta,
            cidade: place?.city,
            estado: place?.state,
            especialidade_nome: procedure?.name,
          })}
          {...rest}
        >
          <Button startIcon={buttonIcon} className="h-12" {...styleProps}>
            {title}
          </Button>
        </Link>
      )}
    </>
  );
}
